import appConfig from "@config/app-config.json";

/**
 * Coordinates in Mapbox's API order: longitude, latitude.
 */
export type MapboxLngLat = readonly [longitude: number, latitude: number];

/**
 * Bounding box in Mapbox's API order:
 * min longitude, min latitude, max longitude, max latitude.
 */
export type MapboxBoundingBox = readonly [
	minLongitude: number,
	minLatitude: number,
	maxLongitude: number,
	maxLatitude: number,
];

/**
 * Geographic feature types supported by Mapbox Geocoding v6.
 *
 * `block` and `secondary_address` may appear in responses, but are not valid
 * filters for every request type.
 */
export type MapboxGeocodingFeatureType =
	| "country"
	| "region"
	| "postcode"
	| "district"
	| "place"
	| "locality"
	| "neighborhood"
	| "street"
	| "block"
	| "address"
	| "secondary_address";

export type MapboxForwardGeocodingFeatureType = Exclude<
	MapboxGeocodingFeatureType,
	"block"
>;

export type MapboxReverseGeocodingFeatureType = Exclude<
	MapboxGeocodingFeatureType,
	"block" | "secondary_address"
>;

export type MapboxGeocodingWorldview =
	"ar" | "cn" | "in" | "jp" | "ma" | "rs" | "ru" | "tr" | "us";

export type MapboxGeocodingAccuracy =
	| "rooftop"
	| "parcel"
	| "point"
	| "interpolated"
	| "approximate"
	| "intersection";

export type MapboxGeocodingMatchCode =
	"matched" | "unmatched" | "not_applicable" | "inferred" | "plausible";

export type MapboxGeocodingMatchConfidence =
	"exact" | "high" | "medium" | "low";

export interface MapboxGeocodingTranslation {
	language: string;
	name: string;
}

export interface MapboxGeocodingContextItem {
	mapbox_id: string;
	name: string;
	alternate?: {
		mapbox_id: string;
		name: string;
	};
	translations?: Record<string, MapboxGeocodingTranslation>;
	wikidata_id?: string;
}

export interface MapboxGeocodingAddressContextItem extends MapboxGeocodingContextItem {
	address_number?: string;
	street_name?: string;
}

export interface MapboxGeocodingSecondaryAddressContextItem extends MapboxGeocodingContextItem {
	designator?: string;
	identifier?: string;
	extrapolated?: boolean;
}

export interface MapboxGeocodingRegionContextItem extends MapboxGeocodingContextItem {
	region_code?: string;
	region_code_full?: string;
}

export interface MapboxGeocodingCountryContextItem extends MapboxGeocodingContextItem {
	country_code?: string;
	country_code_alpha_3?: string;
}

/**
 * Parent geographic hierarchy for a geocoding result.
 */
export interface MapboxGeocodingContext {
	address?: MapboxGeocodingAddressContextItem;
	secondary_address?: MapboxGeocodingSecondaryAddressContextItem;
	street?: MapboxGeocodingContextItem;
	block?: MapboxGeocodingContextItem;
	neighborhood?: MapboxGeocodingContextItem;
	locality?: MapboxGeocodingContextItem;
	postcode?: MapboxGeocodingContextItem;
	place?: MapboxGeocodingContextItem;
	district?: MapboxGeocodingContextItem;
	region?: MapboxGeocodingRegionContextItem;
	country?: MapboxGeocodingCountryContextItem;
}

export interface MapboxGeocodingCoordinates {
	longitude: number;
	latitude: number;
	accuracy: MapboxGeocodingAccuracy;
}

export interface MapboxGeocodingFeatureProperties {
	mapbox_id: string;
	feature_type: MapboxGeocodingFeatureType;
	name: string;
	name_preferred: string;
	place_formatted: string;
	full_address: string;
	context?: MapboxGeocodingContext;
	coordinates: MapboxGeocodingCoordinates;
	bbox?: MapboxBoundingBox;
	match_code?: Partial<
		Record<
			| "address_number"
			| "street"
			| "postcode"
			| "place"
			| "region"
			| "locality"
			| "country"
			| "secondary_address",
			MapboxGeocodingMatchCode
		>
	> & {
		confidence: MapboxGeocodingMatchConfidence;
	};
}

/**
 * GeoJSON Point feature returned by Mapbox Geocoding v6.
 */
export interface MapboxGeocodingFeature {
	id: string;
	type: "Feature";
	geometry: {
		type: "Point";
		coordinates: MapboxLngLat;
	};
	properties: MapboxGeocodingFeatureProperties;
}

/**
 * GeoJSON FeatureCollection returned by Mapbox Geocoding v6.
 */
export interface MapboxGeocodingResponse {
	type: "FeatureCollection";
	features: MapboxGeocodingFeature[];
	attribution: string;
}

interface MapboxGeocodingErrorResponse {
	message?: string;
}

export class MapboxGeocodingError extends Error {
	constructor(
		public readonly status: number,
		public readonly response: MapboxGeocodingErrorResponse | undefined,
	) {
		super(
			response?.message ??
				`Mapbox geocoding request failed with status ${status}`,
		);
		this.name = "MapboxGeocodingError";
	}
}

type QueryParamValue =
	string | number | boolean | readonly string[] | readonly number[];

function setQueryParam(
	params: URLSearchParams,
	key: string,
	value: QueryParamValue | undefined,
) {
	if (value === undefined) {
		return;
	}

	if (Array.isArray(value)) {
		params.set(key, value.join(","));
	} else {
		params.set(key, String(value));
	}
}

interface MapboxForwardGeocodingOptions {
	/**
	 * Whether the result is intended for permanent storage.
	 *
	 * The default is temporary geocoding. Only set this when the Mapbox account
	 * is allowed to store geocoding results.
	 */
	permanent?: boolean;
	/**
	 * Whether Mapbox should return prefix matches while a user is typing.
	 *
	 * Enabled by Mapbox by default. Each keystroke request is billable.
	 */
	autocomplete?: boolean;
	bbox?: MapboxBoundingBox;
	/**
	 * Limit results to one or more ISO 3166 alpha-2 country codes.
	 */
	country?: string | readonly string[];
	/**
	 * IETF language tag or ordered list of language tags.
	 */
	language?: string | readonly string[];
	limit?: number;
	/**
	 * Bias results toward a coordinate or the user's reverse-IP location.
	 */
	proximity?: MapboxLngLat | "ip";
	types?:
		| MapboxForwardGeocodingFeatureType
		| readonly MapboxForwardGeocodingFeatureType[];
	worldview?: MapboxGeocodingWorldview;
}

function addForwardOptions(
	params: URLSearchParams,
	options: MapboxForwardGeocodingOptions,
) {
	setQueryParam(params, "country", options.country);
	setQueryParam(params, "permanent", options.permanent);
	setQueryParam(params, "autocomplete", options.autocomplete);
	setQueryParam(params, "bbox", options.bbox);
	setQueryParam(params, "language", options.language);
	setQueryParam(params, "limit", options.limit);
	setQueryParam(params, "proximity", options.proximity);
	setQueryParam(params, "types", options.types);
	setQueryParam(params, "worldview", options.worldview);
}

interface MapboxReverseGeocodingSharedOptions {
	/**
	 * Whether the result is intended for permanent storage.
	 *
	 * The default is temporary geocoding. Only set this when the Mapbox account
	 * is allowed to store geocoding results.
	 */
	permanent?: boolean;
	country?: string | readonly string[];
	/**
	 * IETF language tag or ordered list of language tags.
	 */
	language?: string | readonly string[];
	worldview?: MapboxGeocodingWorldview;
	/**
	 * Include USPS ZIP+4 in the postcode context for US results.
	 */
	plus4Code?: boolean;
}

/**
 * Reverse geocoding options.
 *
 * Mapbox requires `limit` to be paired with exactly one `types` value, so this
 * type models that constraint.
 */
export type MapboxReverseGeocodingOptions =
	| (MapboxReverseGeocodingSharedOptions & {
			limit?: undefined;
			types?:
				| MapboxReverseGeocodingFeatureType
				| readonly MapboxReverseGeocodingFeatureType[];
	  })
	| (MapboxReverseGeocodingSharedOptions & {
			limit: number;
			types: MapboxReverseGeocodingFeatureType;
	  });

function addReverseOptions(
	params: URLSearchParams,
	options: MapboxReverseGeocodingOptions,
) {
	setQueryParam(params, "permanent", options.permanent);
	setQueryParam(params, "country", options.country);
	setQueryParam(params, "language", options.language);
	setQueryParam(params, "limit", options.limit);
	setQueryParam(params, "types", options.types);
	setQueryParam(params, "worldview", options.worldview);
	setQueryParam(params, "plus4_code", options.plus4Code);
}

async function requestGeocodingEndpoint(
	path: "/search/geocode/v6/forward" | "/search/geocode/v6/reverse",
	queryParams: URLSearchParams,
): Promise<MapboxGeocodingResponse> {
	const url = new URL(path, appConfig.mapboxEndpoint);
	url.search = queryParams.toString();

	const response = await fetch(url);
	if (!response.ok) {
		let errorResponse: MapboxGeocodingErrorResponse | undefined;
		try {
			errorResponse =
				(await response.json()) as unknown as MapboxGeocodingErrorResponse;
		} catch {
			errorResponse = undefined;
		}

		throw new MapboxGeocodingError(response.status, errorResponse);
	}

	return (await response.json()) as unknown as MapboxGeocodingResponse;
}

/**
 * Forward geocode a free-form search string using Mapbox Geocoding v6.
 *
 * Returned features are ordered by relevance. The query text must follow
 * Mapbox's limits: at most 256 characters, at most 20 tokens, and no semicolon.
 */
export function mapboxForwardGeocode(
	mapboxToken: string,
	query: string,
	options: MapboxForwardGeocodingOptions = {},
): Promise<MapboxGeocodingResponse> {
	const queryParams = new URLSearchParams({
		access_token: mapboxToken,
		q: query,
	});

	addForwardOptions(queryParams, options);

	return requestGeocodingEndpoint("/search/geocode/v6/forward", queryParams);
}

/**
 * Reverse geocode a longitude/latitude pair using Mapbox Geocoding v6.
 *
 * Returned features are ordered from the most specific feature to the least
 * specific feature that overlaps the queried coordinates.
 */
export function mapboxReverseGeocode(
	mapboxToken: string,
	coordinates: MapboxLngLat,
	options: MapboxReverseGeocodingOptions = {},
): Promise<MapboxGeocodingResponse> {
	const queryParams = new URLSearchParams({
		access_token: mapboxToken,
		longitude: String(coordinates[0]),
		latitude: String(coordinates[1]),
	});

	addReverseOptions(queryParams, options);

	return requestGeocodingEndpoint("/search/geocode/v6/reverse", queryParams);
}
